import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import bibliographyItemsService from '../services/bibliographyItemsService';
import publicationsService from '../services/publicationsService';
import {BibliographyItem} from '../types/BibliographyItem';
import {Publication} from '../types/Publication';
import {DataTable, DataTableFilterMeta} from 'primereact/datatable';
import {Column, ColumnFilterElementTemplateOptions} from 'primereact/column';
import {Button} from 'primereact/button';
import {Dropdown, DropdownChangeEvent} from 'primereact/dropdown';
import {useNavigate} from "react-router-dom";
import {handleError} from "../../../common/utils/errorHandling";
import AdminFormHeader from "../../../layout/admin/AdminFormHeader";
import {AdminButton} from "../../../layout/admin";
import {FilterMatchMode} from "primereact/api";
import {useAdminConfirmDialog} from "../../../common/hooks/useAdminConfirmDialog";
import BibliographyItemImportDialog from './BibliographyItemImportDialog';
import {useToast} from "../../../common/contexts/ToastContext";

const BibliographyItemsList: React.FC = () => {
  const {t} = useTranslation();
  const {showError, showSuccess} = useToast();
  const {showConfirmDialog} = useAdminConfirmDialog();
  const [bibliographyItems, setBibliographyItems] = useState<BibliographyItem[]>([]);
  const [publications, setPublications] = useState<Publication[]>([]);
  const [importDialogVisible, setImportDialogVisible] = useState(false);
  const navigate = useNavigate();
  const [filters, setFilters] = useState<DataTableFilterMeta>({
    abbreviation: {value: null, matchMode: FilterMatchMode.CONTAINS},
    content: {value: null, matchMode: FilterMatchMode.CONTAINS},
    'publication.name': {value: null, matchMode: FilterMatchMode.EQUALS}
  });

  useEffect(() => {
    fetchBibliographyItems();
    fetchPublications();
  }, []);

  const fetchBibliographyItems = async (): Promise<void> => {
    const bibliographyItemsData = await bibliographyItemsService.getAllBibliographyItems();
    setBibliographyItems(bibliographyItemsData);
  };

  const fetchPublications = async (): Promise<void> => {
    const publicationsData = await publicationsService.getAllPublications();
    setPublications(publicationsData);
  };

  const handleDelete = async (bibliographyItem: BibliographyItem): Promise<void> => {
    showConfirmDialog({
      message: t('admin.common.messages.confirmDeleteWithName', {name: bibliographyItem.abbreviation}),
      accept: async () => {
        await handleError(
          async () => {
            await bibliographyItemsService.deleteBibliographyItem(bibliographyItem.id);
            await fetchBibliographyItems();
          },
          t('admin.bibliographyItems.errors.deleting')
        );
      }
    });
  };

  const handleEdit = (bibliographyItem: BibliographyItem) => {
    navigate(`${bibliographyItem.id}/edit`);
  };

  const handleCreateNew = () => {
    navigate('new');
  };

  const handleImport = () => {
    setImportDialogVisible(true);
  };

  const handleImportComplete = async () => {
    await fetchBibliographyItems();
  };

  const publicationFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
    return (
      <Dropdown
        value={options.value}
        options={publications}
        optionLabel="name"
        optionValue="name"
        placeholder={t('admin.bibliographyItems.filters.filterByPublication')}
        onChange={(e: DropdownChangeEvent) => options.filterApplyCallback(e.value)}
        showClear
      />
    );
  };

  return (
    <>
      <AdminFormHeader
        title={t('admin.bibliographyItems.title.list')}
        subtitle={t('admin.bibliographyItems.subtitle.list')}
      />

      <div className="flex gap-2 mb-4">
        <AdminButton
          onClick={handleCreateNew}
          variant="black"
          label={t('admin.bibliographyItems.actions.createNew')}
        />
        <AdminButton
          onClick={handleImport}
          variant="white"
          label={t('admin.common.actions.importCsv')}
        />
      </div>

      <DataTable
        value={bibliographyItems}
        stripedRows
        paginator
        rows={10}
        filterDisplay="row"
        filters={filters}
        columnResizeMode="fit"
        resizableColumns
        showGridlines
      >
        <Column
          sortField="abbreviation"
          header={t('admin.bibliographyItems.columns.abbreviation')}
          sortable
          filter
          filterField="abbreviation"
          filterPlaceholder={t('admin.bibliographyItems.filters.searchByAbbreviation')}
          showFilterMenu={false}
          showClearButton={false}
          body={(bibliographyItem: BibliographyItem) => (
            <strong>{bibliographyItem.abbreviation}</strong>
          )}/>
        <Column
          sortField="content"
          header={t('admin.bibliographyItems.columns.content')}
          sortable
          filter
          filterField="content"
          filterPlaceholder={t('admin.bibliographyItems.filters.searchByContent')}
          showFilterMenu={false}
          showClearButton={false}
          bodyStyle={{whiteSpace: 'normal', wordWrap: 'break-word'}}
          body={(bibliographyItem: BibliographyItem) => (
            <span dangerouslySetInnerHTML={{__html: bibliographyItem.content || ''}} className="whitespace-pre-wrap"/>
          )}/>
        <Column
          sortField="publication.name"
          header={t('admin.bibliographyItems.columns.publication')}
          sortable
          filter
          filterField="publication.name"
          showFilterMenu={false}
          filterElement={publicationFilterTemplate}
          body={(bibliographyItem: BibliographyItem) => (
            <span>{bibliographyItem.publication?.name || '-'}</span>
          )}/>
        <Column header={t('admin.common.columns.actions')} body={(bibliographyItem: BibliographyItem) => (
          <div>
            <Button
              onClick={() => handleEdit(bibliographyItem)}
              icon="fa fa-edit"
              severity="info"
              size="small"
              outlined
            />
            <Button
              onClick={() => handleDelete(bibliographyItem)}
              icon="fa fa-trash"
              severity="danger"
              size="small"
              outlined
              className="ml-2"
            />
          </div>
        )}/>
      </DataTable>

      <BibliographyItemImportDialog
        visible={importDialogVisible}
        onHide={() => setImportDialogVisible(false)}
        onImportComplete={handleImportComplete}
      />
    </>
  );
};

export default BibliographyItemsList;


